import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft } from 'react-icons/fa';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './AuthPage.css';

const AuthPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [isNewUser, setIsNewUser] = useState(null);
  const [step, setStep] = useState('selectUser');
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    address: '',
    ward: '',
    area: '',
    password: '',
    confirmPassword: '',
    role: 'CITIZEN',
    otp: '',
  });

  /* =====================================================
     HANDLE INPUT
  ===================================================== */

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'mobile') {
      setFormData({
        ...formData,
        mobile: value
          .replace(/\D/g, '')
          .slice(0, 10),
      });
      return;
    }

    if (name === 'otp') {
      setFormData({
        ...formData,
        otp: value
          .replace(/\D/g, '')
          .slice(0, 6),
      });
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  /* =====================================================
     USER TYPE
  ===================================================== */

  const handleUserTypeSelect = (isNew) => {
    setIsNewUser(isNew);
    setStep('form');
  };

  /* =====================================================
     VALIDATE REGISTRATION
  ===================================================== */

  const validateRegistration = () => {
    const name = formData.name.trim();
    const mobile = formData.mobile.trim();
    const address = formData.address.trim();
    const ward = formData.ward.trim();
    const area = formData.area.trim();

    if (
      !name ||
      !mobile ||
      !address ||
      !ward ||
      !area ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      toast.error('Please fill all fields.');
      return false;
    }

    /* ================= NAME ================= */

    if (name.length < 2) {
      toast.error(
        'Name must contain at least 2 characters.'
      );
      return false;
    }

    if (name.length > 50) {
      toast.error(
        'Name cannot exceed 50 characters.'
      );
      return false;
    }

    if (!/^[A-Za-zÀ-ÿ\s.'-]+$/.test(name)) {
      toast.error(
        'Name can contain letters, spaces, apostrophes and hyphens only.'
      );
      return false;
    }

    /* ================= MOBILE ================= */

    if (!/^[6-9][0-9]{9}$/.test(mobile)) {
      toast.error(
        'Enter a valid 10-digit mobile number.'
      );
      return false;
    }

    /* ================= ADDRESS ================= */

    if (address.length < 5) {
      toast.error(
        'Please enter a valid address.'
      );
      return false;
    }

    /* ================= WARD ================= */

    if (ward.length < 1) {
      toast.error(
        'Please enter your ward.'
      );
      return false;
    }

    /* ================= AREA ================= */

    if (area.length < 2) {
      toast.error(
        'Please enter a valid area.'
      );
      return false;
    }

    /* ================= PASSWORD ================= */

    if (formData.password.length < 8) {
      toast.error(
        'Password must contain at least 8 characters.'
      );
      return false;
    }

    if (!/[A-Z]/.test(formData.password)) {
      toast.error(
        'Password must contain at least one uppercase letter.'
      );
      return false;
    }

    if (!/[a-z]/.test(formData.password)) {
      toast.error(
        'Password must contain at least one lowercase letter.'
      );
      return false;
    }

    if (!/[0-9]/.test(formData.password)) {
      toast.error(
        'Password must contain at least one number.'
      );
      return false;
    }

    /* ================= CONFIRM PASSWORD ================= */

    if (
      formData.password !==
      formData.confirmPassword
    ) {
      toast.error(
        'Passwords do not match.'
      );
      return false;
    }

    return true;
  };

  /* =====================================================
     REGISTER CITIZEN
  ===================================================== */

  const handleRegister = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    if (loading) return;

    if (!validateRegistration()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name.trim(),
        mobile: formData.mobile.trim(),
        address: formData.address.trim(),
        ward: formData.ward.trim(),
        area: formData.area.trim(),

        // Required for pending registration
        password: formData.password,

        // Citizen web app only
        role: 'CITIZEN',
      };

      console.log('REGISTER DATA =>', {
        ...payload,
        password: '********',
      });

      /*
       * IMPORTANT:
       * Citizen Cloudflare API URL already ends with /api.
       *
       * Therefore use /auth/register here.
       */
      const response = await api.post(
        '/auth/register',
        payload
      );

      console.log(
        'REGISTER RESPONSE =>',
        response.data
      );

      if (!response.data?.success) {
        toast.error(
          response.data?.message ||
            'Registration failed'
        );
        return;
      }

      toast.success(
        response.data?.message ||
          'OTP sent successfully.'
      );

      setStep('otp');

    } catch (error) {
      console.log(
        'REGISTER ERROR =>',
        error?.response?.data ||
          error?.message ||
          error
      );

      if (
        error?.response?.status === 409
      ) {
        toast.error(
          error?.response?.data?.message ||
            'Mobile number already registered.'
        );
        return;
      }

      toast.error(
        error?.response?.data?.message ||
          'Registration failed'
      );

    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     REQUEST OTP FOR EXISTING CITIZEN
  ===================================================== */

  const handleRequestOTP = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    if (loading) return;

    const mobile =
      formData.mobile.trim();

    if (!/^[6-9][0-9]{9}$/.test(mobile)) {
      toast.error(
        'Enter a valid 10-digit mobile number.'
      );
      return;
    }

    setLoading(true);

    try {
      /*
       * IMPORTANT:
       * Citizen Cloudflare API URL already ends with /api.
       *
       * Therefore use /auth/request-otp here.
       */
      const response = await api.post(
        '/auth/request-otp',
        {
          mobile,
        }
      );

      console.log(
        'REQUEST OTP RESPONSE =>',
        response.data
      );

      if (!response.data?.success) {
        toast.error(
          response.data?.message ||
            'Failed to send OTP'
        );
        return;
      }

      toast.success(
        response.data?.message ||
          'OTP sent successfully.'
      );

      setStep('otp');

    } catch (error) {
      console.log(
        'REQUEST OTP ERROR =>',
        error?.response?.data ||
          error?.message ||
          error
      );

      toast.error(
        error?.response?.data?.message ||
          'Failed to send OTP'
      );

    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     VERIFY OTP
     CITIZEN ONLY
  ===================================================== */

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!/^[0-9]{6}$/.test(formData.otp)) {
      toast.error(
        'Please enter a valid 6-digit OTP.'
      );
      return;
    }

    setLoading(true);

    try {
      /*
       * IMPORTANT:
       * Citizen Cloudflare API URL already ends with /api.
       *
       * Therefore use /auth/verify-otp here.
       */
      const response = await api.post(
        '/auth/verify-otp',
        {
          mobile:
            formData.mobile.trim(),
          otp: formData.otp,
        }
      );

      const data = response.data;

      console.log(
        'VERIFY OTP RESPONSE =>',
        data
      );

      if (!data?.success) {
        toast.error(
          data?.message ||
            'OTP verification failed'
        );
        return;
      }

      /*
       * CITIZEN WEB APP
       *
       * Citizen login is OTP-only.
       * There is NO 2FA handling here.
       */

      if (!data?.token) {
        toast.error(
          'Authentication token was not received.'
        );
        return;
      }

      /* =================================================
         BUILD USER DATA
      ================================================= */

      const userData =
        data?.user || {
          id: data?.userId,
          role:
            data?.role ||
            'ROLE_CITIZEN',
          mobile:
            formData.mobile.trim(),
        };

      /* =================================================
         SAVE LOGIN
      ================================================= */

      login(
        data.token,
        userData
      );

      toast.success(
        'Login successful!'
      );

      /* =================================================
         REDIRECT
      ================================================= */

      if (type === 'complaint') {
        navigate('/complaint/new');
      } else if (type === 'request') {
        navigate('/request/new');
      } else if (type === 'suggestion') {
        navigate('/suggestion/new');
      } else {
        navigate('/dashboard');
      }

    } catch (error) {
      console.log(
        'VERIFY OTP ERROR =>',
        error?.response?.data ||
          error?.message ||
          error
      );

      toast.error(
        error?.response?.data?.message ||
          'OTP verification failed'
      );

    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     RESEND OTP
  ===================================================== */

  const handleResendOTP = async () => {
    if (loading) return;

    if (isNewUser) {
      await handleRegister({
        preventDefault: () => {},
      });
    } else {
      await handleRequestOTP({
        preventDefault: () => {},
      });
    }
  };

  /* =====================================================
     UI
  ===================================================== */

  return (
    <div className="auth-page">

      <div className="auth-container">

        {/* BACK BUTTON */}

        <button
          className="auth-back-btn"
          onClick={() => navigate('/')}
          disabled={loading}
        >
          <FaArrowLeft /> Back
        </button>

        {/* TITLE */}

        <h2 className="auth-title">
          {type === 'complaint' &&
            'Book Complaint'}

          {type === 'request' &&
            'Service Request'}

          {type === 'suggestion' &&
            'Submit Suggestion'}
        </h2>

        {/* =================================================
            SELECT NEW / EXISTING USER
        ================================================= */}

        {step === 'selectUser' && (
          <div className="user-type-selection">

            <h3>
              Are you a new user or existing user?
            </h3>

            <div className="user-type-buttons">

              <button
                className="btn btn-primary"
                onClick={() =>
                  handleUserTypeSelect(true)
                }
              >
                New User
              </button>

              <button
                className="btn btn-outline"
                onClick={() =>
                  handleUserTypeSelect(false)
                }
              >
                Existing User
              </button>

            </div>

          </div>
        )}

        {/* =================================================
            NEW CITIZEN REGISTRATION
        ================================================= */}

        {step === 'form' &&
          isNewUser && (
            <form
              onSubmit={handleRegister}
              className="auth-form"
            >

              <h3>
                Register New Citizen Account
              </h3>

              {/* NAME */}

              <div className="form-group">

                <label>
                  Name *
                </label>

                <input
                  type="text"
                  name="name"
                  className="form-control"
                  value={formData.name}
                  onChange={handleChange}
                  maxLength="50"
                  autoComplete="name"
                  required
                />

              </div>

              {/* MOBILE */}

              <div className="form-group">

                <label>
                  Mobile Number *
                </label>

                <input
                  type="tel"
                  name="mobile"
                  className="form-control"
                  pattern="[6-9][0-9]{9}"
                  placeholder="10-digit mobile number"
                  value={formData.mobile}
                  onChange={handleChange}
                  maxLength="10"
                  autoComplete="tel"
                  required
                />

              </div>

              {/* ADDRESS */}

              <div className="form-group">

                <label>
                  Address *
                </label>

                <textarea
                  name="address"
                  className="form-control"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />

              </div>

              {/* WARD */}

              <div className="form-group">

                <label>
                  Ward *
                </label>

                <input
                  type="text"
                  name="ward"
                  className="form-control"
                  value={formData.ward}
                  onChange={handleChange}
                  required
                />

              </div>

              {/* AREA */}

              <div className="form-group">

                <label>
                  Area *
                </label>

                <input
                  type="text"
                  name="area"
                  className="form-control"
                  value={formData.area}
                  onChange={handleChange}
                  required
                />

              </div>

              {/* PASSWORD */}

              <div className="form-group">

                <label>
                  Password *
                </label>

                <input
                  type="password"
                  name="password"
                  className="form-control"
                  value={formData.password}
                  onChange={handleChange}
                  minLength="8"
                  autoComplete="new-password"
                  required
                />

                <small>
                  Minimum 8 characters with
                  uppercase, lowercase and a number.
                </small>

              </div>

              {/* CONFIRM PASSWORD */}

              <div className="form-group">

                <label>
                  Confirm Password *
                </label>

                <input
                  type="password"
                  name="confirmPassword"
                  className="form-control"
                  value={
                    formData.confirmPassword
                  }
                  onChange={handleChange}
                  minLength="8"
                  autoComplete="new-password"
                  required
                />

              </div>

              {/* REGISTER */}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading
                  ? 'Registering...'
                  : 'Register & Get OTP'}
              </button>

            </form>
          )}

        {/* =================================================
            EXISTING CITIZEN
        ================================================= */}

        {step === 'form' &&
          !isNewUser && (
            <form
              onSubmit={handleRequestOTP}
              className="auth-form"
            >

              <h3>
                Login with Mobile
              </h3>

              <div className="form-group">

                <label>
                  Mobile Number *
                </label>

                <input
                  type="tel"
                  name="mobile"
                  className="form-control"
                  pattern="[6-9][0-9]{9}"
                  placeholder="10-digit mobile number"
                  value={formData.mobile}
                  onChange={handleChange}
                  maxLength="10"
                  autoComplete="tel"
                  required
                />

              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading
                  ? 'Sending...'
                  : 'Send OTP'}
              </button>

            </form>
          )}

        {/* =================================================
            OTP VERIFICATION
        ================================================= */}

        {step === 'otp' && (
          <form
            onSubmit={handleVerifyOTP}
            className="auth-form"
          >

            <h3>
              Verify OTP
            </h3>

            <p className="otp-info">
              Enter the 6-digit OTP sent to{' '}
              {formData.mobile}
            </p>

            <div className="form-group">

              <label>
                OTP *
              </label>

              <input
                type="text"
                name="otp"
                className="form-control"
                pattern="[0-9]{6}"
                maxLength="6"
                inputMode="numeric"
                placeholder="Enter 6-digit OTP"
                value={formData.otp}
                onChange={handleChange}
                autoComplete="one-time-code"
                required
              />

            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading
                ? 'Verifying...'
                : 'Verify & Login'}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              style={{
                marginTop: '10px',
              }}
              onClick={handleResendOTP}
              disabled={loading}
            >
              Resend OTP
            </button>

          </form>
        )}

      </div>

    </div>
  );
};

export default AuthPage;